import { ComponentProps, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { Box, HStack, IconButton, Pressable, View } from 'native-base';
import { orderBy } from 'lodash';

import { Maximize01Icon, Modal, SettingsIcon, Text } from '@pimm/base';
import { MainNavigationParams } from '@app/navigations/root';
import { useAppModules } from '@app/features/app';
import { DashboardModalType, useDashboard } from '@app/features/dashboard';
import { useSiteConfig } from '@app/features/store-core';
import { useWidgetModules, WidgetCokeFreeStyle, WidgetModule, WidgetSpeedOfService } from '@app/features/widgets';
import { WidgetCenter } from '@app/features/widgets/components/widget-center';
import { WidgetEditor } from '@app/features/widgets/components/widget-editor';
import { SortableList } from '@app/features/dnd/components/sortable-list.web';
import { WidgetWeather } from '@app/features/widgets/components/widget-weather';
import { WidgetSalesLabor } from '@app/features/widgets';
import { WidgetCelebrations } from '@app/features/widgets/components/widget-celebrations';
import { WidgetDeliveryMonitor } from '@app/features/widgets/components/widget-delivery-monitor';
import { WidgetTemperatures } from '@app/features/widgets/components/widget-temperatures';
import { useModalFocus } from '@app/hooks/modal-focus.hook';

export const WIDGET_COMPONENTS: WidgetModule[] = [
  { identifier: 'weather', component: WidgetWeather },
  { identifier: 'celebrations', component: WidgetCelebrations, payload: { modalType: DashboardModalType.EmployeeCelebration } },
  { identifier: 'driveThruTimer', component: WidgetSpeedOfService },
  { identifier: 'cokeFreeStyle', component: WidgetCokeFreeStyle, payload: { modalType: DashboardModalType.CokeFreestyle } },
  { identifier: 'deliveryMonitor', component: WidgetDeliveryMonitor, payload: { modalType: DashboardModalType.DeliveryMonitor } },
  { identifier: 'equipment', component: WidgetTemperatures },
  { identifier: 'salesTracker', title: 'Sales Tracker', component: WidgetSalesLabor },
];

type WidgetsProps = ComponentProps<typeof View> & {};

export const FavoriteWidgets = (props: WidgetsProps) => {
  const { t } = useTranslation();
  const navigation = useNavigation<NavigationProp<MainNavigationParams>>();
  const { screens } = useAppModules();
  const { siteConfig } = useSiteConfig();
  const { widgets, updateWidgets } = useWidgetModules();
  const { toggleModal } = useDashboard();
  const modal = useModalFocus<{ type: 'editor' | 'center' }>();

  // Only show favorite widgets that either true or undefined but not false
  const favoriteWidgets = useMemo(() => {
    const favorites = orderBy(
      widgets?.filter(_ => _?.isFavorite),
      ['favoriteSeq'],
      ['asc'],
    );
    if (favorites.length === 0) return widgets;
    return favorites;
  }, [widgets]);

  const handlePressWidget = useCallback((widget: WidgetModule, payload?: { dispenserId?: string }) => {
    // navigate to modal screen
    if (widget?.payload?.modalType) {
      const modalPayload: Record<string, unknown> = {};
      if (payload?.dispenserId) modalPayload.dispenserId = payload.dispenserId;
      toggleModal({ type: widget.payload.modalType, payload: modalPayload });
      return;
    }

    // navigate to screen
    const screen = screens.find(_ => _.identifierId === widget.identifier)
    if (screen?.name) {
      navigation.navigate(screen.name as keyof MainNavigationParams, { siteId: siteConfig?.id });
    }
  }, [navigation, siteConfig?.id, screens, toggleModal]); // prettier-ignore

  return (
    <View flex={1} {...props}>
      <SortableList
        _contentBodyProps={{ space: 2, px: 2.5, pb: 2.5 }}
        data={favoriteWidgets}
        keyExtractor={item => item.identifier}
        onChange={updateWidgets}
        headerComponent={
          <HStack alignItems="center" justifyContent="space-between" py={2} px={2} bg="gray.200">
            <IconButton
              variant="unstyled"
              rounded="lg"
              p={1}
              icon={<SettingsIcon size="18px" color="gray.600" />}
              _pressed={{ bg: 'gray.300' }}
              onPress={() => modal.setOpen({ type: 'editor' })}
            />

            <Box flex={1} justifyContent="center">
              <Text fontWeight={600} size="xl" color="black" textAlign="center">
                {t('app:widget_center')}
              </Text>
            </Box>

            <IconButton
              variant="unstyled"
              rounded="lg"
              p={1}
              icon={<Maximize01Icon size={4} color="gray.600" />}
              _pressed={{ bg: 'gray.300' }}
              onPress={() => modal.setOpen({ type: 'center' })}
            />
          </HStack>
        }
        renderItem={_item => {
          const Widget = _item.component! as React.ComponentType<any>;

          return (
            <Pressable delayLongPress={500} onPress={() => handlePressWidget(_item)}>
              <Widget
                key={_item.identifier}
                title={_item.title}
                titleTranslations={_item.titleTranslations}
                onPressDispenser={dispenser => handlePressWidget(_item, { dispenserId: dispenser.dispenserId })}
              />
            </Pressable>
          );
        }}
      />

      <Modal size="xl" hideClose noPadding _content={{ w: '98%', h: '90%', rounded: '2xl' }} isOpen={modal.isOpen}>
        {modal.payload?.type === 'center' && <WidgetCenter onClose={() => modal.setHide()} />}
        {modal.payload?.type === 'editor' && <WidgetEditor onClose={() => modal.setHide()} />}
      </Modal>
    </View>
  );
};
